import React, { useState, useMemo, useEffect } from 'react';
import CalcShell from '../CalcShell';

/**
 * Universal Unit Converter — 17 unit families.
 * Each family lists units by their multiplier to the family's SI base unit.
 * Temperature, Angle (turn/grad) and Fuel Economy use custom conversions.
 */
const FAMILIES = {
  Length: { base: 'meter', units: {
    'kilometer (km)': 1000, 'meter (m)': 1, 'centimeter (cm)': 0.01, 'millimeter (mm)': 0.001,
    'micrometer (µm)': 1e-6, 'nanometer (nm)': 1e-9,
    'mile (mi)': 1609.344, 'yard (yd)': 0.9144, 'foot (ft)': 0.3048, 'inch (in)': 0.0254,
    'nautical mile': 1852, 'furlong': 201.168, 'chain': 20.1168, 'rod': 5.0292,
    'astronomical unit (AU)': 1.495978707e11, 'light-year (ly)': 9.4607e15, 'parsec (pc)': 3.0857e16,
    'angstrom (Å)': 1e-10
  }},
  Area: { base: 'square meter', units: {
    'square kilometer (km²)': 1e6, 'hectare (ha)': 1e4, 'square meter (m²)': 1,
    'square centimeter (cm²)': 1e-4, 'square millimeter (mm²)': 1e-6,
    'square mile': 2.58999e6, 'acre': 4046.856, 'square yard (yd²)': 0.836127,
    'square foot (ft²)': 0.092903, 'square inch (in²)': 0.00064516,
    'are (a)': 100, 'barn (physics)': 1e-28
  }},
  Volume: { base: 'liter', units: {
    'cubic meter (m³)': 1000, 'liter (L)': 1, 'milliliter (mL)': 0.001, 'cubic centimeter (cc)': 0.001,
    'cubic inch (in³)': 0.0163871, 'cubic foot (ft³)': 28.3168, 'cubic yard (yd³)': 764.555,
    'US gallon': 3.78541, 'US quart': 0.946353, 'US pint': 0.473176, 'US cup': 0.24,
    'US fluid ounce': 0.0295735, 'US tablespoon': 0.0147868, 'US teaspoon': 0.00492892,
    'Imperial gallon': 4.54609, 'Imperial quart': 1.13652, 'Imperial pint': 0.568261,
    'barrel (oil)': 158.987
  }},
  'Mass / Weight': { base: 'gram', units: {
    'metric ton (t)': 1e6, 'kilogram (kg)': 1000, 'gram (g)': 1, 'milligram (mg)': 0.001,
    'microgram (µg)': 1e-6,
    'US ton': 907185, 'UK ton': 1016047, 'pound (lb)': 453.592, 'ounce (oz)': 28.3495,
    'stone (st)': 6350.29, 'grain (gr)': 0.06479891, 'carat (ct)': 0.2, 'slug': 14593.9,
    'atomic mass unit (u)': 1.66054e-24
  }},
  Temperature: { base: 'celsius', units: {
    '°Celsius': 'C', '°Fahrenheit': 'F', 'Kelvin (K)': 'K', 'Rankine (°R)': 'R', 'Réaumur (°Ré)': 'Re'
  }},
  Speed: { base: 'meters per second', units: {
    'meters per second (m/s)': 1, 'kilometers per hour (km/h)': 0.277778,
    'miles per hour (mph)': 0.44704, 'feet per second (ft/s)': 0.3048,
    'knot (nmi/h)': 0.514444, 'Mach (STP)': 340.29, 'speed of light (c)': 2.998e8
  }},
  Time: { base: 'second', units: {
    'nanosecond (ns)': 1e-9, 'microsecond (µs)': 1e-6, 'millisecond (ms)': 0.001,
    'second (s)': 1, 'minute (min)': 60, 'hour (h)': 3600, 'day (d)': 86400,
    'week': 604800, 'month (30.44 d)': 2629746, 'year (365.25 d)': 31557600, 'decade': 3.15576e8, 'century': 3.15576e9
  }},
  Angle: { base: 'radian', units: {
    'radian (rad)': 1, 'degree (°)': Math.PI/180, 'gradian (gon)': Math.PI/200,
    'arcminute (′)': Math.PI/10800, 'arcsecond (″)': Math.PI/648000, 'turn / revolution': 2*Math.PI
  }},
  Pressure: { base: 'pascal', units: {
    'pascal (Pa)': 1, 'kilopascal (kPa)': 1000, 'megapascal (MPa)': 1e6,
    'bar': 1e5, 'millibar (mbar)': 100,
    'atmosphere (atm)': 101325, 'torr / mmHg': 133.322,
    'psi (lbf/in²)': 6894.757, 'ksi (kip/in²)': 6.895e6,
    'inch of mercury (inHg)': 3386.39, 'inch of water': 249.089
  }},
  Energy: { base: 'joule', units: {
    'joule (J)': 1, 'kilojoule (kJ)': 1000, 'megajoule (MJ)': 1e6,
    'calorie (cal)': 4.184, 'kilocalorie (kcal / Cal)': 4184,
    'watt-hour (Wh)': 3600, 'kilowatt-hour (kWh)': 3.6e6,
    'BTU': 1055.06, 'therm': 1.05506e8,
    'electronvolt (eV)': 1.602176634e-19, 'foot-pound (ft·lbf)': 1.35582
  }},
  Power: { base: 'watt', units: {
    'watt (W)': 1, 'kilowatt (kW)': 1000, 'megawatt (MW)': 1e6,
    'horsepower (mechanical)': 745.699872, 'horsepower (metric)': 735.49875, 'horsepower (electrical)': 746,
    'BTU / hour': 0.29307107, 'foot-pound / minute': 0.022597,
    'kilocalorie / hour': 1.163
  }},
  'Digital Storage': { base: 'byte', units: {
    'bit': 0.125, 'byte (B)': 1,
    'kilobyte (KB, 1000 B)': 1000, 'megabyte (MB)': 1e6, 'gigabyte (GB)': 1e9,
    'terabyte (TB)': 1e12, 'petabyte (PB)': 1e15,
    'kibibyte (KiB, 1024 B)': 1024, 'mebibyte (MiB)': 1024**2, 'gibibyte (GiB)': 1024**3,
    'tebibyte (TiB)': 1024**4, 'pebibyte (PiB)': 1024**5
  }},
  'Data Rate': { base: 'bit per second', units: {
    'bit/s (bps)': 1, 'kilobit/s (kbps)': 1000, 'megabit/s (Mbps)': 1e6, 'gigabit/s (Gbps)': 1e9, 'terabit/s (Tbps)': 1e12,
    'byte/s (B/s)': 8, 'kilobyte/s (KB/s)': 8e3, 'megabyte/s (MB/s)': 8e6, 'gigabyte/s (GB/s)': 8e9
  }},
  Force: { base: 'newton', units: {
    'newton (N)': 1, 'kilonewton (kN)': 1000, 'meganewton (MN)': 1e6,
    'dyne': 1e-5, 'kilogram-force (kgf)': 9.80665,
    'pound-force (lbf)': 4.44822, 'ounce-force (ozf)': 0.278014, 'ton-force (US)': 8896.44
  }},
  Torque: { base: 'newton-meter', units: {
    'newton-meter (N·m)': 1, 'kilonewton-meter (kN·m)': 1000,
    'foot-pound (ft·lbf)': 1.35582, 'inch-pound (in·lbf)': 0.112985,
    'kilogram-force meter (kgf·m)': 9.80665, 'dyne-centimeter': 1e-7
  }},
  Frequency: { base: 'hertz', units: {
    'hertz (Hz)': 1, 'kilohertz (kHz)': 1000, 'megahertz (MHz)': 1e6, 'gigahertz (GHz)': 1e9,
    'RPM (revolutions per minute)': 1/60, 'radian per second (rad/s)': 1/(2*Math.PI)
  }},
  'Fuel Economy': { base: 'km per liter', units: {
    'miles per US gallon (mpg)': 'mpgUS',
    'miles per UK gallon (mpg UK)': 'mpgUK',
    'kilometers per liter (km/L)': 'kmL',
    'liters per 100 km (L/100 km)': 'L100km',
    'liters per mile (L/mi)': 'Lmi'
  }},
  Density: { base: 'kilogram per cubic meter', units: {
    'kilogram per cubic meter (kg/m³)': 1,
    'gram per cubic centimeter (g/cm³)': 1000,
    'gram per milliliter (g/mL)': 1000,
    'pound per cubic foot (lb/ft³)': 16.0185,
    'pound per cubic inch (lb/in³)': 27679.9,
    'ounce per cubic inch (oz/in³)': 1729.99,
    'metric ton per cubic meter (t/m³)': 1000,
    'slug per cubic foot': 515.379,
  }},
  'Electric Current': { base: 'ampere', units: {
    'ampere (A)': 1, 'milliampere (mA)': 0.001, 'microampere (µA)': 1e-6,
    'kiloampere (kA)': 1000, 'abampere (biot)': 10, 'statampere': 3.335641e-10,
  }},
  'Sound Level': { base: 'decibel', units: {
    'decibel (dB)': 1, 'bel (B)': 10, 'neper (Np)': 8.685889638,
  }},
  Illuminance: { base: 'lux', units: {
    'lux (lx)': 1, 'foot-candle (fc)': 10.7639,
    'phot': 10000, 'nox': 0.001,
  }},
  Radioactivity: { base: 'becquerel', units: {
    'becquerel (Bq)': 1, 'kilobecquerel (kBq)': 1000, 'megabecquerel (MBq)': 1e6,
    'gigabecquerel (GBq)': 1e9, 'curie (Ci)': 3.7e10, 'millicurie (mCi)': 3.7e7, 'microcurie (µCi)': 37000,
  }},
  Viscosity: { base: 'pascal-second', units: {
    'pascal-second (Pa·s)': 1, 'poise (P)': 0.1, 'centipoise (cP)': 0.001,
    'pound-force second per square foot': 47.8803, 'pound per foot-second': 1.48816,
  }},
  'Angular Speed': { base: 'radian per second', units: {
    'radian per second (rad/s)': 1, 'degree per second (°/s)': Math.PI / 180,
    'revolution per second (rps)': 2 * Math.PI, 'RPM (revolutions per minute)': 2 * Math.PI / 60,
    'revolution per hour': 2 * Math.PI / 3600,
  }}
};

function convertTemp(v, from, to) {
  let c = 0;
  if (from === 'C') c = v; else if (from === 'F') c = (v - 32) * 5/9;
  else if (from === 'K') c = v - 273.15; else if (from === 'R') c = (v - 491.67) * 5/9;
  else if (from === 'Re') c = v * 5/4;
  if (to === 'C') return c;
  if (to === 'F') return c * 9/5 + 32;
  if (to === 'K') return c + 273.15;
  if (to === 'R') return (c + 273.15) * 9/5;
  if (to === 'Re') return c * 4/5;
  return c;
}
// Fuel Economy: convert everything through km/L for storage
function toKmL(v, unit) {
  if (v === 0) return 0;
  if (unit === 'kmL') return v;
  if (unit === 'mpgUS') return v * 0.425144;
  if (unit === 'mpgUK') return v * 0.354006;
  if (unit === 'L100km') return 100 / v;
  if (unit === 'Lmi') return 1.609344 / v;
  return v;
}
function fromKmL(v, unit) {
  if (v === 0) return 0;
  if (unit === 'kmL') return v;
  if (unit === 'mpgUS') return v / 0.425144;
  if (unit === 'mpgUK') return v / 0.354006;
  if (unit === 'L100km') return 100 / v;
  if (unit === 'Lmi') return 1.609344 / v;
  return v;
}

const STORAGE_KEY = 'calc-conversion-favs';

export default function ConversionCalc() {
  const [family, setFamily] = useState('Length');
  const opts = FAMILIES[family].units;
  const keys = Object.keys(opts);
  const [from, setFrom] = useState(keys[0]);
  const [to, setTo] = useState(keys[1] || keys[0]);
  const [val, setVal] = useState(1);
  const [q, setQ] = useState('');
  const [favs, setFavs] = useState([]);

  useEffect(() => { try { const r = localStorage.getItem(STORAGE_KEY); if (r) setFavs(JSON.parse(r)); } catch {} }, []);
  useEffect(() => { try { localStorage.setItem(STORAGE_KEY, JSON.stringify(favs)); } catch {} }, [favs]);

  useEffect(() => {
    const k = Object.keys(FAMILIES[family].units);
    setFrom(k[0]); setTo(k[1] || k[0]);
  }, [family]);

  const convert = (v, fromKey, toKey) => {
    if (family === 'Temperature') return convertTemp(Number(v), opts[fromKey], opts[toKey]);
    if (family === 'Fuel Economy') {
      const kmL = toKmL(Number(v), opts[fromKey]);
      return fromKmL(kmL, opts[toKey]);
    }
    const inBase = Number(v) * opts[fromKey];
    return inBase / opts[toKey];
  };

  const result = useMemo(() => convert(val, from, to), [family, val, from, to]);
  const swap = () => { setFrom(to); setTo(from); };

  const filteredKeys = q ? keys.filter(k => k.toLowerCase().includes(q.toLowerCase())) : keys;

  // Full table of conversions for every unit in the family
  const table = useMemo(() => keys.map(k => ({ k, v: convert(val, from, k) })), [val, from, family]);

  const addFav = () => {
    const entry = { family, from, to, val: Number(val) };
    if (favs.some(f => f.family === entry.family && f.from === entry.from && f.to === entry.to)) return;
    setFavs([entry, ...favs].slice(0, 8));
  };

  return (
    <CalcShell look="cnet" title="Unit Converter" subtitle="Convert between 130+ units across 17 families — length, mass, volume, temperature, energy, data, fuel economy and more.">
      <div className="flex gap-2 mb-4 flex-wrap">
        {Object.keys(FAMILIES).map(f => (
          <button key={f} onClick={() => setFamily(f)} className={`px-3 py-1.5 rounded-full text-xs font-semibold ${family === f ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`} data-testid={`fam-${f}`}>
            {f}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-3 items-end">
        <div className="field lg:col-span-1"><label>Value</label><input type="number" value={val} onChange={e => setVal(e.target.value)} data-testid="conv-value"/></div>
        <div className="field lg:col-span-2"><label>From</label>
          <select value={from} onChange={e => setFrom(e.target.value)} data-testid="conv-from">{filteredKeys.map(k => <option key={k}>{k}</option>)}</select>
        </div>
        <div className="flex items-end pb-2">
          <button onClick={swap} className="w-full px-3 py-2 border rounded-lg text-sm hover:bg-slate-50">⇄ Swap</button>
        </div>
        <div className="field lg:col-span-1"><label>To</label>
          <select value={to} onChange={e => setTo(e.target.value)} data-testid="conv-to">{filteredKeys.map(k => <option key={k}>{k}</option>)}</select>
        </div>
      </div>

      <div className="field mt-3"><label className="text-xs">Filter units</label>
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="e.g. inch, gallon…" className="text-sm"/>
      </div>

      <div className="result-panel mt-4">
        <div className="result-title">{val} {from} =</div>
        <div className="result-value" data-testid="conv-result">
          {typeof result === 'number' && isFinite(result) ? result.toLocaleString('en-US', { maximumFractionDigits: 10 }) : '—'} {to}
        </div>
      </div>

      <div className="flex gap-2 mt-3">
        <button onClick={addFav} className="px-3 py-1.5 rounded-lg border text-sm">★ Save conversion</button>
        <button onClick={() => setVal(1)} className="px-3 py-1.5 rounded-lg border text-sm">Reset value</button>
      </div>

      {favs.length > 0 && (
        <div className="mt-4">
          <h4 className="text-sm font-semibold mb-2">Saved</h4>
          <div className="flex gap-2 flex-wrap">
            {favs.map((f, i) => (
              <button key={i} onClick={() => { setFamily(f.family); setTimeout(() => { setFrom(f.from); setTo(f.to); setVal(f.val); }, 30); }} className="px-3 py-1 rounded-full bg-slate-100 text-xs hover:bg-slate-200">
                {f.val} {f.from.split(' ')[0]} → {f.to.split(' ')[0]}
              </button>
            ))}
            <button onClick={() => setFavs([])} className="px-3 py-1 rounded-full text-xs text-rose-600">Clear</button>
          </div>
        </div>
      )}

      {/* Full family table */}
      <div className="mt-6">
        <h3 className="text-sm font-semibold mb-2">{val} {from} in all {family} units</h3>
        <div className="max-h-[420px] overflow-y-auto border border-slate-200 rounded-xl">
          <table className="data-table !mb-0">
            <thead className="sticky top-0 bg-slate-50"><tr><th>Unit</th><th className="text-right">Value</th></tr></thead>
            <tbody>{table.map(r => (
              <tr key={r.k}><td>{r.k}</td>
                <td className="text-right font-mono">{Number(r.v).toLocaleString('en-US', { maximumFractionDigits: 8 })}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      </div>
    </CalcShell>
  );
}
